import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Calendar, Image, Mail, Settings, LogOut,
  Plus, Trash2, Edit, Eye, EyeOff, Save, X, Check, Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin API with auth
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const adminApi = {
  login: (data) => axios.post(`${API}/admin/login`, data),
  setup: () => axios.post(`${API}/admin/setup`),
  // Events
  getEvents: () => axios.get(`${API}/events`),
  createEvent: (data) => axios.post(`${API}/admin/events`, data, getAuthHeaders()),
  updateEvent: (id, data) => axios.put(`${API}/admin/events/${id}`, data, getAuthHeaders()),
  deleteEvent: (id) => axios.delete(`${API}/admin/events/${id}`, getAuthHeaders()),
  // Gallery
  getGallery: () => axios.get(`${API}/gallery`),
  addGalleryItem: (data) => axios.post(`${API}/admin/gallery`, data, getAuthHeaders()),
  deleteGalleryItem: (id) => axios.delete(`${API}/admin/gallery/${id}`, getAuthHeaders()),
  // Contact
  getContacts: () => axios.get(`${API}/admin/contacts`, getAuthHeaders()),
  markContactRead: (id) => axios.put(`${API}/admin/contacts/${id}/read`, {}, getAuthHeaders()),
  // Newsletter
  getNewsletterSubs: () => axios.get(`${API}/admin/newsletter`, getAuthHeaders()),
  // Settings
  getSettings: () => axios.get(`${API}/settings`),
  updateSettings: (data) => axios.put(`${API}/admin/settings`, data, getAuthHeaders()),
  // Upload
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API}/admin/upload`, formData, {
      ...getAuthHeaders(),
      headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
    });
  },
};

// ============== IMAGE UPLOADER COMPONENT ==============
const ImageUploader = ({ value, onChange, label, accept = "image/*" }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const res = await adminApi.uploadFile(file);
      const url = `${BACKEND_URL}${res.data.url}`;
      onChange(url);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">{label}</label>
      <div className="space-y-3">
        {value && (
          <div className="relative border border-white/10 p-2 rounded">
            <img src={value} alt="Preview" className="w-full h-32 object-cover rounded" onError={(e) => { e.target.style.display = 'none'; }} />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-3 right-3 p-1 bg-black/60 text-white rounded-full hover:bg-black/80"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <label className={`flex items-center justify-center gap-2 w-full py-3 px-4 border border-dashed border-white/20 text-sm cursor-pointer hover:border-white/40 transition-colors rounded ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <Plus size={16} className="text-text-muted" />
          <span className="text-text-muted">{uploading ? "Uploading..." : value ? "Replace Image" : "Upload Image"}</span>
          <input type="file" accept={accept} onChange={handleFileChange} className="hidden" />
        </label>
        {error && <p className="text-red-400 text-xs">{error}</p>}
      </div>
    </div>
  );
};

// Admin pages must not be indexed by search engines
const useNoIndex = () => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    // Override the page-level robots tag
    const existing = document.querySelector('meta[name="robots"]');
    if (existing) existing.content = 'noindex, nofollow';
    return () => {
      document.head.removeChild(meta);
      if (existing) existing.content = 'index, follow';
    };
  }, []);
};

// ============== LOGIN ==============
export const AdminLogin = () => {
  useNoIndex();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.login({ username, password });
      localStorage.setItem("admin_token", res.data.access_token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const handleSetup = async () => {
    try {
      const res = await adminApi.setup();
      alert(`Admin created! Username: ${res.data.username}, Password: ${res.data.password}`);
    } catch (err) {
      alert(err.response?.data?.detail || "Setup failed");
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="font-syne text-3xl font-bold text-white mb-2">Admin Login</h1>
          <p className="text-text-secondary">RROV Goa Management</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-background-paper border border-white/10 p-8">
          <div className="mb-6">
            <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              data-testid="admin-username"
              className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
            />
          </div>
          <div className="mb-6">
            <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password"
              className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
            />
          </div>

          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="btn-primary w-full py-4"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <button
          onClick={handleSetup}
          className="mt-4 text-text-muted text-xs hover:text-white transition-colors w-full text-center"
        >
          First time? Click to setup admin
        </button>
      </motion.div>
    </div>
  );
};

// ============== ADMIN LAYOUT ==============
const AdminLayout = ({ children, activeTab, setActiveTab }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "events", label: "Events", icon: Calendar },
    { id: "gallery", label: "Gallery", icon: Image },
    { id: "contacts", label: "Contacts", icon: Mail },
    { id: "newsletter", label: "Newsletter", icon: Users },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-background-paper border-r border-white/5 flex flex-col">
        <div className="p-6 border-b border-white/5">
          <h1 className="font-syne text-xl font-bold text-white">RROV Admin</h1>
        </div>

        <nav className="flex-1 p-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 mb-2 transition-colors ${
                activeTab === tab.id
                  ? "bg-primary/10 text-primary"
                  : "text-text-secondary hover:text-white hover:bg-white/5"
              }`}
            >
              <tab.icon size={18} />
              <span className="text-sm">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-4 py-3 text-text-secondary hover:text-red-500 transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">{children}</main>
    </div>
  );
};

// ============== DASHBOARD ==============
const Dashboard = () => {
  const [stats, setStats] = useState({ events: 0, gallery: 0, contacts: 0, newsletter: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [events, gallery, contacts, newsletter] = await Promise.all([
          adminApi.getEvents(),
          adminApi.getGallery(),
          adminApi.getContacts(),
          adminApi.getNewsletterSubs(),
        ]);
        setStats({
          events: events.data.length,
          gallery: gallery.data.length,
          contacts: contacts.data.length,
          newsletter: newsletter.data.length,
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: "Events", value: stats.events, color: "primary" },
    { label: "Gallery Items", value: stats.gallery, color: "secondary" },
    { label: "Contact Messages", value: stats.contacts, color: "accent-teal" },
    { label: "Newsletter Subs", value: stats.newsletter, color: "accent-ocean" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h2 className="font-syne text-2xl font-bold text-white mb-8">Dashboard</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div key={stat.label} className="bg-background-paper border border-white/10 p-6">
            <p className="text-text-muted text-xs uppercase tracking-widest mb-2">{stat.label}</p>
            <p className="font-syne text-4xl font-bold text-white">{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const DEFAULT_TABLES = [
  { category: "VVIP", tables_range: "V1 — V11", capacity: "6–10 guests", price: "" },
  { category: "Members' Lounge", tables_range: "King, Joker, K1 — K4", capacity: "4–8 guests", price: "" },
  { category: "Cabana", tables_range: "C1 — C6", capacity: "8–12 guests", price: "" },
  { category: "Queen", tables_range: "Q1 — Q2", capacity: "4–6 guests", price: "" },
];

// ============== EVENTS MANAGER ==============
const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "", slug: "", date: "", time: "", description: "",
    image_url: "", vaycray_slug: "", vaycray_table_slug: "", ticket_url: "", is_featured: false,
    is_weekly_residency: false, day_of_week: "", tagline: "",
    tables: DEFAULT_TABLES.map(t => ({ ...t }))
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await adminApi.getEvents();
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await adminApi.updateEvent(editingEvent.id, formData);
      } else {
        await adminApi.createEvent(formData);
      }
      fetchEvents();
      resetForm();
    } catch (err) {
      alert(err.response?.data?.detail || "Error saving event");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await adminApi.deleteEvent(id);
      fetchEvents();
    } catch (err) {
      alert("Error deleting event");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      slug: event.slug,
      date: event.date,
      time: event.time,
      description: event.description,
      image_url: event.image_url,
      vaycray_slug: event.vaycray_slug || "",
      vaycray_table_slug: event.vaycray_table_slug || "",
      ticket_url: event.ticket_url || "",
      is_featured: event.is_featured,
      is_weekly_residency: event.is_weekly_residency,
      day_of_week: event.day_of_week || "",
      tagline: event.tagline || "",
      tables: event.tables && event.tables.length > 0
        ? event.tables.map(t => ({ category: t.category, tables_range: t.tables_range, capacity: t.capacity, price: t.price || "" }))
        : DEFAULT_TABLES.map(t => ({ ...t }))
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      title: "", slug: "", date: "", time: "", description: "",
      image_url: "", vaycray_slug: "", vaycray_table_slug: "", ticket_url: "", is_featured: false,
      is_weekly_residency: false, day_of_week: "", tagline: "",
      tables: DEFAULT_TABLES.map(t => ({ ...t }))
    });
  };

  const updateTable = (idx, field, value) => {
    const updated = [...formData.tables];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormData({ ...formData, tables: updated });
  };

  const addTable = () => {
    setFormData({
      ...formData,
      tables: [...formData.tables, { category: "", tables_range: "", capacity: "", price: "" }]
    });
  };

  const removeTable = (idx) => {
    setFormData({ ...formData, tables: formData.tables.filter((_, i) => i !== idx) });
  };

  return (
    <div data-testid="admin-events">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-syne text-2xl font-bold text-white">Events</h2>
        <button
          onClick={() => setShowForm(true)}
          data-testid="add-event-btn"
          className="btn-primary px-6 py-3 flex items-center gap-2"
        >
          <Plus size={18} /> Add Event
        </button>
      </div>

      {/* Events List */}
      <div className="space-y-4">
        {events.map((event) => (
          <div key={event.id} className="bg-background-paper border border-white/10 p-4 flex items-center gap-4">
            <img src={event.image_url} alt={event.title} className="w-20 h-20 object-cover" />
            <div className="flex-1">
              <h3 className="font-syne text-lg font-bold text-white">{event.title}</h3>
              <p className="text-text-secondary text-sm">{event.date} at {event.time}</p>
              {event.is_featured && <span className="text-primary text-xs">Featured</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleEdit(event)} className="p-2 text-text-secondary hover:text-white">
                <Edit size={18} />
              </button>
              <button onClick={() => handleDelete(event.id)} className="p-2 text-text-secondary hover:text-red-500">
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
        {events.length === 0 && (
          <p className="text-text-secondary text-center py-8">No events yet. Add your first event!</p>
        )}
      </div>

      {/* Event Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-background-paper border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-syne text-xl font-bold text-white">
                {editingEvent ? "Edit Event" : "Add Event"}
              </h3>
              <button onClick={resetForm} className="text-text-secondary hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    placeholder="event-url-slug"
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Date *</label>
                  <input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    placeholder="Saturday, January 25"
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Time *</label>
                  <input
                    type="text"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                    placeholder="9 PM onwards"
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Tagline</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="Short catchy line"
                  className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary resize-none"
                />
              </div>

              <ImageUploader
                label="Event Poster *"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
              />

              <div>
                <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Vaycray Ticket Slug</label>
                <input
                  type="text"
                  value={formData.vaycray_slug}
                  onChange={(e) => setFormData({ ...formData, vaycray_slug: e.target.value })}
                  placeholder="e.g. disco-brunch-lazy-cat-cafe-goa"
                  className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary text-sm"
                />
                <p className="text-text-muted text-xs mt-1">For ticket purchases. From vaycray.com/events/<strong>this-part</strong></p>
              </div>

              <div>
                <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Vaycray Table Booking Slug</label>
                <input
                  type="text"
                  value={formData.vaycray_table_slug}
                  onChange={(e) => setFormData({ ...formData, vaycray_table_slug: e.target.value })}
                  placeholder="e.g. disco-brunch-lazy-cat-cafe-goa"
                  className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary text-sm"
                />
                <p className="text-text-muted text-xs mt-1">For VIP table reservations. Opens Vaycray table booking embed.</p>
              </div>

              <div>
                <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Ticket URL (Fallback)</label>
                <input
                  type="url"
                  value={formData.ticket_url}
                  onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })}
                  placeholder="https://in.bookmyshow.com/events/..."
                  className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary text-sm"
                />
                <p className="text-text-muted text-xs mt-1">Used only if Vaycray slug is empty. Opens in new tab.</p>
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                  <span className="text-white text-sm">Featured Event</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_weekly_residency}
                    onChange={(e) => setFormData({ ...formData, is_weekly_residency: e.target.checked })}
                    className="w-5 h-5 accent-primary"
                  />
                  <span className="text-white text-sm">Weekly Residency</span>
                </label>
              </div>

              {formData.is_weekly_residency && (
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Day of Week</label>
                  <select
                    value={formData.day_of_week}
                    onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  >
                    <option value="">Select day</option>
                    {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Table Pricing Section */}
              <div className="border border-white/10 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-text-muted text-xs uppercase tracking-widest font-semibold">Table Pricing (Display)</label>
                  <button type="button" onClick={addTable} className="text-primary text-xs flex items-center gap-1 hover:text-white transition-colors">
                    <Plus size={14} /> Add Category
                  </button>
                </div>
                <p className="text-text-muted text-xs">Prices shown on the website. Actual booking is handled by Vaycray via the table slug above.</p>
                {formData.tables.map((table, idx) => (
                  <div key={idx} className="bg-background border border-white/5 p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-xs font-medium">Category {idx + 1}</span>
                      <button type="button" onClick={() => removeTable(idx)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text" placeholder="Category name (e.g. VVIP)" value={table.category}
                        onChange={(e) => updateTable(idx, "category", e.target.value)}
                        className="bg-background-paper border border-white/10 px-3 py-2 text-white text-sm focus:outline-none focus:border-primary"
                      />
                      <input
                        type="text" placeholder="Tables (e.g. V1 — V11)" value={table.tables_range}
                        onChange={(e) => updateTable(idx, "tables_range", e.target.value)}
                        className="bg-background-paper border border-white/10 px-3 py-2 text-white text-sm focus:outline-none focus:border-primary"
                      />
                      <input
                        type="text" placeholder="Capacity (e.g. 6–10 guests)" value={table.capacity}
                        onChange={(e) => updateTable(idx, "capacity", e.target.value)}
                        className="bg-background-paper border border-white/10 px-3 py-2 text-white text-sm focus:outline-none focus:border-primary"
                      />
                      <input
                        type="text" placeholder="Price (e.g. ₹50,000)" value={table.price}
                        onChange={(e) => updateTable(idx, "price", e.target.value)}
                        className="bg-background-paper border border-white/10 px-3 py-2 text-white text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                ))}
                {formData.tables.length === 0 && (
                  <p className="text-text-secondary text-xs text-center py-2">No table categories. Click "Add Category" above.</p>
                )}
              </div>

              <div className="flex gap-4 pt-4">
                <button type="submit" className="btn-primary px-8 py-3 flex items-center gap-2">
                  <Save size={18} /> {editingEvent ? "Update" : "Create"} Event
                </button>
                <button type="button" onClick={resetForm} className="btn-outline px-8 py-3">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== GALLERY MANAGER ==============
const GalleryManager = () => {
  const [gallery, setGallery] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    image_url: "", caption: "", item_type: "photo", video_url: "", order: 0
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await adminApi.getGallery();
      setGallery(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        // Delete old and re-add since API may not have gallery update
        await adminApi.deleteGalleryItem(editingItem.id);
      }
      await adminApi.addGalleryItem(formData);
      fetchGallery();
      resetForm();
    } catch (err) {
      alert("Error saving gallery item");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      image_url: item.image_url,
      caption: item.caption || "",
      item_type: item.item_type || "photo",
      video_url: item.video_url || "",
      order: item.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await adminApi.deleteGalleryItem(id);
      fetchGallery();
    } catch (err) {
      alert("Error deleting item");
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({ image_url: "", caption: "", item_type: "photo", video_url: "", order: 0 });
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-syne text-2xl font-bold text-white">Gallery</h2>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          data-testid="add-gallery-btn"
          className="btn-primary px-6 py-3 flex items-center gap-2"
        >
          <Plus size={18} /> Add Item
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {gallery.map((item) => (
          <div key={item.id} className="relative group">
            <img src={item.image_url} alt={item.caption} className="w-full aspect-square object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                onClick={() => handleEdit(item)}
                className="p-2 bg-white/20 text-white hover:bg-white/40 transition-colors"
              >
                <Edit size={18} />
              </button>
              <button
                onClick={() => handleDelete(item.id)}
                className="p-2 bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>
            {item.caption && (
              <p className="text-text-secondary text-xs mt-1 truncate">{item.caption}</p>
            )}
          </div>
        ))}
      </div>

      {gallery.length === 0 && (
        <p className="text-text-secondary text-center py-8">No gallery items. Add your first image!</p>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background-paper border border-white/10 w-full max-w-md">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-syne text-xl font-bold text-white">
                {editingItem ? "Edit Gallery Item" : "Add Gallery Item"}
              </h3>
              <button onClick={resetForm} className="text-text-secondary hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <ImageUploader
                label="Image *"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
              />
              <div>
                <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Caption</label>
                <input
                  type="text"
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Type</label>
                  <select
                    value={formData.item_type}
                    onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  >
                    <option value="photo">Photo</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
              {formData.item_type === "video" && (
                <div>
                  <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Video URL</label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="YouTube or video URL"
                    className="w-full bg-background border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
                  />
                </div>
              )}
              <div className="flex gap-4 pt-2">
                <button type="submit" className="btn-primary flex-1 py-3 flex items-center justify-center gap-2">
                  <Save size={18} /> {editingItem ? "Update" : "Add"} Item
                </button>
                <button type="button" onClick={resetForm} className="btn-outline px-6 py-3">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== CONTACTS VIEWER ==============
const ContactsViewer = () => {
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    adminApi.getContacts()
      .then((res) => setContacts(res.data))
      .catch(console.error);
  }, []);

  const markAsRead = async (id) => {
    try {
      await adminApi.markContactRead(id);
      setContacts(contacts.map((c) => c.id === id ? { ...c, is_read: true } : c));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div data-testid="admin-contacts">
      <h2 className="font-syne text-2xl font-bold text-white mb-8">Contact Messages</h2>
      <div className="space-y-4">
        {contacts.map((contact) => (
          <div
            key={contact.id}
            className={`bg-background-paper border p-6 ${contact.is_read ? "border-white/10" : "border-primary/50"}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-bold text-white">{contact.name}</h3>
                <p className="text-text-secondary text-sm">{contact.email}</p>
                {contact.phone && <p className="text-text-muted text-sm">{contact.phone}</p>}
              </div>
              {!contact.is_read && (
                <button
                  onClick={() => markAsRead(contact.id)}
                  className="text-primary hover:text-primary-light text-sm flex items-center gap-1"
                >
                  <Check size={16} /> Mark as read
                </button>
              )}
            </div>
            <p className="text-text-primary">{contact.message}</p>
            <p className="text-text-muted text-xs mt-4">{new Date(contact.created_at).toLocaleString()}</p>
          </div>
        ))}
        {contacts.length === 0 && (
          <p className="text-text-secondary text-center py-8">No messages yet</p>
        )}
      </div>
    </div>
  );
};

// ============== NEWSLETTER VIEWER ==============
const NewsletterViewer = () => {
  const [subscribers, setSubscribers] = useState([]);

  useEffect(() => {
    adminApi.getNewsletterSubs()
      .then((res) => setSubscribers(res.data))
      .catch(console.error);
  }, []);

  return (
    <div data-testid="admin-newsletter">
      <h2 className="font-syne text-2xl font-bold text-white mb-8">Newsletter Subscribers ({subscribers.length})</h2>
      <div className="bg-background-paper border border-white/10">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left p-4 text-text-muted text-xs uppercase tracking-widest">Email</th>
              <th className="text-left p-4 text-text-muted text-xs uppercase tracking-widest">Name</th>
              <th className="text-left p-4 text-text-muted text-xs uppercase tracking-widest">Date</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((sub) => (
              <tr key={sub.id} className="border-b border-white/5">
                <td className="p-4 text-white">{sub.email}</td>
                <td className="p-4 text-text-secondary">{sub.name || "-"}</td>
                <td className="p-4 text-text-muted text-sm">{new Date(sub.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscribers.length === 0 && (
          <p className="text-text-secondary text-center py-8">No subscribers yet</p>
        )}
      </div>
    </div>
  );
};

// ============== SETTINGS ==============
const SettingsManager = () => {
  const [settings, setSettings] = useState({
    hero_title: "Experience RROV Goa",
    hero_subtitle: "Where the beach meets the beat",
    hero_image_url: "",
    hero_video_url: "",
    about_text: "",
    instagram_url: "",
    vip_iframe_code: "",
    contact_email: "",
    contact_phone: "",
    address: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.getSettings()
      .then((res) => setSettings({ ...settings, ...res.data }))
      .catch(console.error);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      alert("Settings saved!");
    } catch (err) {
      alert("Error saving settings");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-settings">
      <h2 className="font-syne text-2xl font-bold text-white mb-8">Site Settings</h2>

      <div className="space-y-6 max-w-2xl">
        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Hero Title</label>
          <input
            type="text"
            value={settings.hero_title}
            onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Hero Subtitle</label>
          <input
            type="text"
            value={settings.hero_subtitle}
            onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
          />
        </div>

        <ImageUploader
          label="Hero Banner Image"
          value={settings.hero_image_url}
          onChange={(url) => setSettings({ ...settings, hero_image_url: url })}
        />

        <ImageUploader
          label="Hero Banner Video (optional, overrides image)"
          value={settings.hero_video_url}
          onChange={(url) => setSettings({ ...settings, hero_video_url: url })}
          accept="video/mp4,video/quicktime"
        />

        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">About Text</label>
          <textarea
            value={settings.about_text}
            onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
            rows={4}
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary resize-none"
          />
        </div>

        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Instagram URL</label>
          <input
            type="url"
            value={settings.instagram_url}
            onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
            placeholder="https://instagram.com/rrovgoa"
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">VIP Booking Iframe Code</label>
          <textarea
            value={settings.vip_iframe_code}
            onChange={(e) => setSettings({ ...settings, vip_iframe_code: e.target.value })}
            rows={3}
            placeholder='<iframe src="..." ...></iframe>'
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary resize-none font-mono text-sm"
          />
        </div>

        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Contact Email</label>
          <input
            type="email"
            value={settings.contact_email}
            onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Contact Phone</label>
          <input
            type="tel"
            value={settings.contact_phone}
            onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-text-muted text-xs uppercase tracking-widest mb-2">Address</label>
          <input
            type="text"
            value={settings.address}
            onChange={(e) => setSettings({ ...settings, address: e.target.value })}
            className="w-full bg-background-paper border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-primary"
          />
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary px-8 py-3 flex items-center gap-2"
        >
          <Save size={18} /> {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

// ============== ADMIN DASHBOARD MAIN ==============
export const AdminDashboard = () => {
  useNoIndex();
  const [activeTab, setActiveTab] = useState("dashboard");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
    }
  }, [navigate]);

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return <Dashboard />;
      case "events": return <EventsManager />;
      case "gallery": return <GalleryManager />;
      case "contacts": return <ContactsViewer />;
      case "newsletter": return <NewsletterViewer />;
      case "settings": return <SettingsManager />;
      default: return <Dashboard />;
    }
  };

  return (
    <AdminLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      {renderContent()}
    </AdminLayout>
  );
};
